/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { CreateItpSchema, type CreateItpInput } from '@therapy/shared-validators';
import { Plus } from 'lucide-react';
import { type ChangeEvent, type SyntheticEvent, useCallback, type JSX } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { type Client } from '../../lib/api/resources.js';
import { templatesApi } from '../../lib/api/templates.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useCreatePlan, useTaxonomy } from '../../lib/queries/itp.js';
import { useTemplates } from '../../lib/queries/templates.js';

import { EMPTY_GOAL, emptyToUndefined, templateToFormGoals } from './plan-form-utils.js';
import { type Errors, GoalRow, type Register } from './plan-goal-row.js';

interface PlanFormProps {
  readonly onDone: (planId: string) => void;
  /** When set, the plan is locked to this client and the picker is hidden. */
  readonly clientId?: string | undefined;
}

/** Plan-level fields: client, name, program type, start/review dates. */
function PlanMetaFields({
  register,
  errors,
  clients,
  hideClient,
}: {
  readonly register: Register;
  readonly errors: Errors;
  readonly clients: readonly Client[];
  readonly hideClient: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      {hideClient && <input type="hidden" {...register('clientId')} />}
      {!hideClient && (
        <div>
          <label
            htmlFor="plan-client"
            className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            {t('therapist.planForm.client')}
          </label>
          <select
            id="plan-client"
            className="focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            defaultValue=""
            aria-invalid={errors.clientId !== undefined}
            {...register('clientId')}
          >
            <option value="" disabled>
              {t('therapist.planForm.selectClient')}
            </option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.fullName}
              </option>
            ))}
          </select>
          {errors.clientId !== undefined && (
            <p role="alert" className="mt-1 text-sm text-red-600">
              {errors.clientId.message}
            </p>
          )}
        </div>
      )}
      <FormField
        id="plan-name"
        type="text"
        label={t('therapist.planForm.name')}
        autoComplete="off"
        error={errors.name?.message}
        registration={register('name')}
      />
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField
          id="plan-program"
          type="text"
          label={t('therapist.planForm.programType')}
          autoComplete="off"
          registration={register('programType', { setValueAs: emptyToUndefined })}
        />
        <FormField
          id="plan-start"
          type="date"
          label={t('therapist.planForm.startDate')}
          autoComplete="off"
          registration={register('startDate', { setValueAs: emptyToUndefined })}
        />
        <FormField
          id="plan-review"
          type="date"
          label={t('therapist.planForm.reviewDate')}
          autoComplete="off"
          registration={register('reviewDate', { setValueAs: emptyToUndefined })}
        />
      </div>
    </>
  );
}

/** Create a therapy plan with its goal set (ITP Builder). Validated by `CreateItpSchema`. */
export function PlanForm({ onDone, clientId }: PlanFormProps): JSX.Element {
  const { t } = useTranslation();
  const clients = useClients();
  const templates = useTemplates();
  const taxonomy = useTaxonomy();
  const create = useCreatePlan();
  const {
    register,
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateItpInput>({
    resolver: zodResolver(CreateItpSchema),
    defaultValues: { clientId: clientId ?? '', name: '', goals: [{ ...EMPTY_GOAL }] },
  });
  const { fields, append, remove, replace } = useFieldArray({ control, name: 'goals' });

  const addGoal = useCallback(() => {
    append({ ...EMPTY_GOAL });
  }, [append]);

  const onTemplate = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const templateId = event.target.value;
      if (templateId === '') {
        return;
      }
      void (async (): Promise<void> => {
        const tpl = await templatesApi.getOne(templateId);
        if (tpl.goals.length > 0) {
          replace(templateToFormGoals(tpl.goals));
        }
      })();
    },
    [replace],
  );

  const submit = useCallback(
    async (values: CreateItpInput): Promise<void> => {
      const plan = await create.mutateAsync(values);
      onDone(plan.id);
    },
    [create, onDone],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <PlanMetaFields
        register={register}
        errors={errors}
        clients={clients.data ?? []}
        hideClient={clientId !== undefined}
      />

      {(templates.data ?? []).length > 0 && (
        <div>
          <label
            htmlFor="plan-template"
            className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            {t('therapist.planForm.fromTemplate')}
          </label>
          <select
            id="plan-template"
            className="focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            defaultValue=""
            onChange={onTemplate}
          >
            <option value="">{t('therapist.planForm.fromTemplateNone')}</option>
            {(templates.data ?? []).map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name} ({tpl.goalCount})
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {t('therapist.planForm.goals')}
          </p>
          <Button type="button" variant="outline" onClick={addGoal}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('therapist.planForm.addGoal')}
          </Button>
        </div>
        {typeof errors.goals?.message === 'string' && (
          <p role="alert" className="text-sm text-red-600">
            {errors.goals.message}
          </p>
        )}
        {fields.map((field, index) => (
          <GoalRow
            key={field.id}
            index={index}
            register={register}
            setValue={setValue}
            errors={errors}
            taxonomy={taxonomy.data ?? []}
            canRemove={fields.length > 1}
            onRemove={remove}
          />
        ))}
      </div>

      {create.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}
      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={isSubmitting || create.isPending}>
          {t('therapist.planForm.submit')}
        </Button>
      </div>
    </form>
  );
}
